import React, { useMemo, useState } from "react";

import { COMPARE_METRICS, CROP_SOIL_METRICS, LOCATION_SOIL_METRICS } from "consts";

import { Flex, Option, Select, Table, TagNormal, Tooltip, Typography } from "components";

import { SoilPropertyOriginTag, SoilPropertyOriginTagLegend } from "pages/admin/soilSight/components/shared/SoilPropertyOriginTag.tsx";
import { AverageRow, LocationRotationRow, LocationRow } from "pages/admin/soilSight/utils/compareTypes.ts";
import { formatValue, valueColor } from "pages/admin/soilSight/utils/compareUtils.ts";
import { soilPropertyOrigin } from "pages/admin/soilSight/utils/soilPropertyOrigin.ts";
import { locationColorMap as buildLocationColorMap, scenarioColor } from "pages/admin/soilSight/utils/soilSightColors";

export const SingleVersionTable = ({ rows }: { rows: AverageRow[] }) => {
  const [filterLocation, setFilterLocation] = useState<string>("all");

  const locations = useMemo(() => [...new Set(rows.map((r) => r.location))].sort(), [rows]);
  const locationColors = useMemo(() => buildLocationColorMap(locations), [locations]);

  const filtered = filterLocation === "all" ? rows : rows.filter((r) => r.location === filterLocation);

  const mainColumns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: AverageRow, b: AverageRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColors[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    {
      title: "Rotation",
      dataIndex: "rotation",
      key: "rotation",
      fixed: "left" as const,
      sorter: (a: AverageRow, b: AverageRow) => a.rotation.localeCompare(b.rotation),
      render: (val: string, row: AverageRow) => (row.rotationCrops ? <Tooltip title={row.rotationCrops}>{val}</Tooltip> : val),
    },
    {
      title: "Scenario",
      dataIndex: "scenario",
      key: "scenario",
      fixed: "left" as const,
      sorter: (a: AverageRow, b: AverageRow) => a.scenario.localeCompare(b.scenario),
      render: (val: string) => <TagNormal text={val} style={{ background: scenarioColor(val), color: "#fff", border: "none" }} />,
    },
    {
      title: "Yrs",
      dataIndex: "matchedYears",
      key: "matchedYears",
      sorter: (a: AverageRow, b: AverageRow) => a.matchedYears - b.matchedYears,
    },
    ...COMPARE_METRICS.map((metric) => ({
      title: (
        <div>
          <div>{metric.label}</div>
          <div className="soil-sight-table-th-unit">{metric.unit}</div>
        </div>
      ),
      key: metric.key,
      align: "right" as const,
      sorter: (a: AverageRow, b: AverageRow) => (a.values[metric.key] ?? -Infinity) - (b.values[metric.key] ?? -Infinity),
      render: (_: unknown, row: AverageRow) => {
        const v = row.values[metric.key] ?? null;
        return <span style={{ color: valueColor(v, metric.higherIsBetter), fontWeight: v !== null && Math.abs(v) > 0.001 ? 600 : 400 }}>{formatValue(v)}</span>;
      },
    })),
  ];

  const rowKey = (row: AverageRow) => `${row.location}|${row.rotation}|${row.scenario}`;
  const rowClassName = (_: AverageRow, index: number) => (index % 2 !== 0 ? "soil-sight-row--striped" : "");

  return (
    <div>
      <div className="soil-sight-table-toolbar">
        <Select label="Location" value={filterLocation} style={{ width: 160 }} onChange={(v: string) => setFilterLocation(v)}>
          <Option value="all">All</Option>
          {locations.map((location) => (
            <Option key={location} value={location}>
              {location}
            </Option>
          ))}
        </Select>
        <span className="soil-sight-table-hint">Values = avg per year. Green = good, red = needs attention.</span>
      </div>

      <Table data={filtered} columns={mainColumns} size="small" pagination={false} rowKey={rowKey} rowClassName={rowClassName} scroll={{ x: true }} />
    </div>
  );
};

export const SingleVersionLocationTable = ({ rows, version }: { rows: LocationRow[]; version: string | null }) => {
  const locationColors = useMemo(() => buildLocationColorMap(rows.map((r) => r.location)), [rows]);

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: LocationRow, b: LocationRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColors[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    ...LOCATION_SOIL_METRICS.map((metric) => ({
      title: (
        <Flex vertical align="center" justify="flex-start">
          <Flex gap="6px" align="center" justify="flex-start">
            <SoilPropertyOriginTag origin={soilPropertyOrigin(metric.key, version)} />
            <div>{metric.label}</div>
          </Flex>
          {metric.unit && <div className="soil-sight-table-th-unit">{metric.unit}</div>}
        </Flex>
      ),
      key: metric.key,
      align: "right" as const,
      sorter: (a: LocationRow, b: LocationRow) => (a.values[metric.key] ?? -Infinity) - (b.values[metric.key] ?? -Infinity),
      render: (_: unknown, row: LocationRow) => formatValue(row.values[metric.key] ?? null),
    })),
  ];

  return (
    <div>
      <div className="soil-sight-table-toolbar">
        <Typography variant="h3">Soil/weather properties per location</Typography>
        <SoilPropertyOriginTagLegend />
      </div>
      <Table data={rows} columns={columns} size="small" pagination={false} rowKey="location" scroll={{ x: true }} />
    </div>
  );
};

export const SingleVersionLocationRotationTable = ({ rows, version }: { rows: LocationRotationRow[]; version: string | null }) => {
  const locations = useMemo(() => [...new Set(rows.map((r) => r.location))].sort(), [rows]);
  const locationColors = useMemo(() => buildLocationColorMap(locations), [locations]);

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: LocationRotationRow, b: LocationRotationRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColors[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    {
      title: "Rotation",
      dataIndex: "rotation",
      key: "rotation",
      fixed: "left" as const,
      sorter: (a: LocationRotationRow, b: LocationRotationRow) => a.rotation.localeCompare(b.rotation),
      render: (val: string, row: LocationRotationRow) => (row.rotationCrops ? <Tooltip title={row.rotationCrops}>{val}</Tooltip> : val),
    },
    ...CROP_SOIL_METRICS.map((metric) => ({
      title: (
        <Flex vertical align="center" justify="flex-start">
          <Flex gap="6px" align="center" justify="flex-start">
            <SoilPropertyOriginTag origin={soilPropertyOrigin(metric.key, version)} />
            <div>{metric.label}</div>
          </Flex>
          {metric.unit && <div className="soil-sight-table-th-unit">{metric.unit}</div>}
        </Flex>
      ),
      key: metric.key,
      align: "right" as const,
      sorter: (a: LocationRotationRow, b: LocationRotationRow) => (a.values[metric.key] ?? -Infinity) - (b.values[metric.key] ?? -Infinity),
      render: (_: unknown, row: LocationRotationRow) => formatValue(row.values[metric.key] ?? null),
    })),
  ];

  const rowKey = (row: LocationRotationRow) => `${row.location}|${row.rotation}`;

  return (
    <div>
      <Flex vertical>
        <Typography variant="h3">Crop properties based on Location and Rotation</Typography>
        <span className="soil-sight-table-hint">Vary by the rotation&apos;s first crop, one row per location and rotation.</span>
      </Flex>
      <Table data={rows} columns={columns} size="small" pagination={false} rowKey={rowKey} scroll={{ x: true }} />
    </div>
  );
};
